// brand-consent-banner — light-DOM custom element for the hand-built consent layer.
// Self-contained: no @theme/* imports. Guarded define (multiple renders must not throw).
//
// Default-deny by design: it NEVER calls setTrackingConsent on load. It only loads the
// Customer Privacy API, shows the bar when no choice has been made yet, and writes consent
// inside click handlers. Reacts to visitorConsentCollected and supports reopening the panel
// from the footer "Preferencias de cookies" control, pre-checked to the saved state.
//
// Progressive enhancement: if window.Shopify is absent the element does nothing — no banner,
// no tracking, which is a compliant default-deny state.
//
// Category map (D-02): analytics -> Analitica (GA4); marketing -> Meta + Google Ads + TikTok.
if (!customElements.get('brand-consent-banner')) {
  customElements.define(
    'brand-consent-banner',
    class extends HTMLElement {
      connectedCallback() {
        // Cache controls once.
        this.bar = this.querySelector('[data-consent-bar]');
        this.panel = this.querySelector('[data-consent-panel]');
        this.analyticsToggle = this.querySelector('[data-toggle-analytics]');
        this.marketingToggle = this.querySelector('[data-toggle-marketing]');
        this.lastFocus = null;

        // Reopen from the footer works even when the privacy API never loads — it just
        // shows the panel markup. Wire it before loadFeatures so it is always live.
        this.onReopen = (e) => {
          if (e.target.closest('[data-consent-reopen]')) {
            e.preventDefault();
            this.openPanel();
          }
        };
        document.addEventListener('click', this.onReopen);

        // Esc closes the panel and restores focus.
        this.onKeydown = (e) => {
          if (e.key === 'Escape' && this.panel && !this.panel.hidden) {
            this.closePanel();
          }
        };
        this.addEventListener('keydown', this.onKeydown);

        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (err) => {
            if (!err) this.init();
          }
        );
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onReopen);
        this.removeEventListener('keydown', this.onKeydown);
      }

      init() {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp) return;

        // Show the bar ONLY when no choice exists yet. Never grants here (default-deny).
        if (typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner()) {
          this.show();
        }

        // Hide on any consent change (the choice was just recorded elsewhere or here).
        document.addEventListener('visitorConsentCollected', () => this.hide());

        // Click wiring — these are the ONLY paths that write consent.
        this.querySelector('[data-accept-all]')?.addEventListener('click', () =>
          this.save(true, true)
        );
        this.querySelector('[data-only-necessary]')?.addEventListener('click', () =>
          this.save(false, false)
        );
        this.querySelector('[data-configure]')?.addEventListener('click', () =>
          this.openPanel()
        );
        this.querySelector('[data-save-prefs]')?.addEventListener('click', () =>
          this.save(!!this.analyticsToggle?.checked, !!this.marketingToggle?.checked)
        );
        this.querySelector('[data-consent-close]')?.addEventListener('click', () =>
          this.closePanel()
        );
      }

      // ONLY called from click handlers — never auto-run (Pitfall 5).
      save(analytics, marketing) {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.setTrackingConsent !== 'function') {
          this.hide();
          return;
        }
        cp.setTrackingConsent(
          {
            analytics: analytics,
            marketing: marketing,
            preferences: false,
            sale_of_data: false,
          },
          () => this.hide()
        );
      }

      openPanel() {
        // Show the bar if it is hidden (footer reopen path), then reveal the panel.
        this.removeAttribute('hidden');
        document.body.classList.add('brand-consent-open');
        if (this.panel) this.panel.hidden = false;

        // Pre-check toggles from saved state. Saved-shape keys verified at impl against
        // the Customer Privacy API; guard both the method and the returned shape.
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        const saved = (cp && typeof cp.currentVisitorConsent === 'function' && cp.currentVisitorConsent()) || {};
        if (this.analyticsToggle) {
          this.analyticsToggle.checked = saved.analytics === true || saved.analytics === 'yes';
        }
        if (this.marketingToggle) {
          this.marketingToggle.checked = saved.marketing === true || saved.marketing === 'yes';
        }

        // Move focus into the panel; remember where it was so we can restore it.
        this.lastFocus = document.activeElement;
        const firstControl = this.panel?.querySelector('input, button');
        firstControl?.focus();
      }

      closePanel() {
        if (this.panel) this.panel.hidden = true;
        // Restore focus to whatever opened the panel.
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
          this.lastFocus.focus();
        }
        this.lastFocus = null;
      }

      show() {
        this.removeAttribute('hidden');
        document.body.classList.add('brand-consent-open');
      }

      hide() {
        if (this.panel) this.panel.hidden = true;
        this.setAttribute('hidden', '');
        document.body.classList.remove('brand-consent-open');
      }
    }
  );
}
